import React, { memo, useRef, useEffect } from 'react';

interface WaveLoadingProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'spinner' | 'wave' | 'pulse' | 'dots';
  color?: 'bright-blue' | 'dark-blue' | 'purple' | 'fleet-monitor';
  text?: string;
  fullScreen?: boolean;
  className?: string;
  style?: React.CSSProperties; // Allow custom styles
  progress?: number; // Progress percentage (0-100)
  progressText?: string; // Custom progress description
}

// Separate pure component for wave animation that never re-renders
const PureWaveAnimation = memo(({ color, size }: { color: string; size: string }) => {
  const getSizeClasses = () => {
    const sizes = {
      sm: 'w-10 h-10',
      md: 'w-16 h-16',
      lg: 'w-28 h-28',
      xl: 'w-40 h-40',
    };
    return sizes[size as keyof typeof sizes] || sizes.md;
  };

  return (
    <div className={`${getSizeClasses()} relative`}>
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Inline styles for animation to ensure it's self-contained */}
        <defs>
          <style>
            {`
              @keyframes waveFlowSmooth {
                0%, 100% { stroke-dashoffset: 0; }
                50% { stroke-dashoffset: 20; }
              }
              .wave-path-smooth {
                fill: none;
                stroke-width: 3;
                stroke-linecap: round;
                stroke-dasharray: 15,5;
                animation: waveFlowSmooth 3s ease-in-out infinite;
              }
            `}
          </style>
        </defs>
        <path 
          className="wave-path-smooth"
          style={{ 
            stroke: color,
            opacity: 1,
            animationDelay: '0s'
          }}
          d="M10,50 Q30,20 50,50 T90,50" 
        />
        <path 
          className="wave-path-smooth"
          style={{ 
            stroke: color,
            opacity: 0.8,
            animationDelay: '0.5s'
          }}
          d="M10,50 Q30,30 50,50 T90,50" 
        />
        <path 
          className="wave-path-smooth"
          style={{ 
            stroke: color,
            opacity: 0.6,
            animationDelay: '1s'
          }}
          d="M10,50 Q30,40 50,50 T90,50" 
        />
        <path 
          className="wave-path-smooth"
          style={{ 
            stroke: color,
            opacity: 0.4,
            animationDelay: '1.5s'
          }}
          d="M10,50 Q30,45 50,50 T90,50" 
        />
      </svg>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if color or size changes (which they shouldn't during loading)
  return prevProps.color === nextProps.color && prevProps.size === nextProps.size;
});

PureWaveAnimation.displayName = 'PureWaveAnimation';

// Separate component for progress bar that updates via DOM manipulation
const ProgressBarComponent = ({ progress, progressText, color }: { 
  progress?: number; 
  progressText?: string; 
  color: string;
}) => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressTextRef = useRef<HTMLSpanElement>(null);
  const progressValueRef = useRef<HTMLSpanElement>(null);
  
  // Use direct DOM updates for smoother progress updates
  useEffect(() => {
    if (progress === undefined) return;
    
    const clampedProgress = Math.max(0, Math.min(100, progress));
    
    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      // Update progress bar width directly via DOM
      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${clampedProgress}%`;
      }
      
      // Update progress text directly via DOM
      if (progressTextRef.current && progressText) {
        progressTextRef.current.textContent = progressText;
      }
      
      // Update progress value directly via DOM
      if (progressValueRef.current) {
        progressValueRef.current.textContent = `${clampedProgress.toFixed(0)}%`;
      }
    });
  }, [progress, progressText]);
  
  if (progress === undefined) return null;

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <span 
          ref={progressTextRef}
          className="text-sm font-medium truncate flex-1" 
          style={{ color }}
        >
          {progressText || 'Processing...'}
        </span>
        <span 
          ref={progressValueRef}
          className="text-sm font-medium whitespace-nowrap" 
          style={{ color }}
        >
          {Math.max(0, Math.min(100, progress)).toFixed(0)}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div 
          ref={progressBarRef}
          className="h-2 rounded-full transition-all duration-200 ease-out"
          style={{ 
            width: '0%', // Initial width, will be updated via DOM
            backgroundColor: color,
            transform: 'translateZ(0)', // Force GPU acceleration
            willChange: 'width', // Optimize for width changes
          }}
        />
      </div>
    </div>
  );
};

const WaveLoading: React.FC<WaveLoadingProps> = ({
  size = 'md',
  variant = 'wave',
  color = 'bright-blue',
  text,
  fullScreen = false,
  className = '',
  style,
  progress,
  progressText,
}) => {
  const getColorValue = (colorName: string) => {
    const colors = {
      'bright-blue': '#004dff',
      'dark-blue': '#0b053b',
      purple: '#353668',
      'fleet-monitor': '#2f2fff',
    };
    return colors[colorName as keyof typeof colors] || colors['bright-blue'];
  };

  const getSizeClasses = () => {
    const sizes = {
      sm: 'w-10 h-10',
      md: 'w-16 h-16',
      lg: fullScreen ? 'w-28 h-28' : 'w-20 h-20',
      xl: fullScreen ? 'w-40 h-40' : 'w-32 h-32',
    };
    return sizes[size];
  };

  const SpinnerVariant = () => (
    <div className={`${getSizeClasses()} relative`}>
      <div
        className="absolute inset-0 border-4 border-transparent rounded-full animate-spin"
        style={{
          borderTopColor: getColorValue(color),
          borderRightColor: getColorValue(color),
          borderBottomColor: `${getColorValue(color)}33`,
          borderLeftColor: `${getColorValue(color)}33`,
        }}
      />
      <div
        className="absolute inset-2 border-2 border-transparent rounded-full animate-spin"
        style={{
          borderTopColor: `${getColorValue(color)}66`,
          borderRightColor: `${getColorValue(color)}66`,
          animationDirection: 'reverse',
          animationDuration: '0.75s',
        }}
      />
    </div>
  );

  const PulseVariant = () => (
    <div className={`${getSizeClasses()} relative`}>
      <div
        className="absolute inset-0 rounded-full animate-ping"
        style={{ backgroundColor: `${getColorValue(color)}33` }}
      />
      <div
        className="absolute inset-2 rounded-full animate-ping"
        style={{
          backgroundColor: `${getColorValue(color)}66`,
          animationDelay: '0.5s',
        }}
      />
      <div
        className="absolute inset-4 rounded-full"
        style={{ backgroundColor: getColorValue(color) }}
      />
    </div>
  );

  const DotsVariant = () => (
    <div className="flex space-x-1">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className="w-3 h-3 rounded-full animate-bounce"
          style={{
            backgroundColor: getColorValue(color),
            animationDelay: `${index * 0.2}s`,
          }}
        />
      ))}
    </div>
  );

  const renderVariant = () => {
    switch (variant) {
      case 'spinner':
        return <SpinnerVariant />;
      case 'wave':
        // Use the pure wave animation component that never re-renders
        return <PureWaveAnimation color={getColorValue(color)} size={size} />;
      case 'pulse':
        return <PulseVariant />;
      case 'dots':
        return <DotsVariant />;
      default:
        return <PureWaveAnimation color={getColorValue(color)} size={size} />;
    }
  };

  const content = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {renderVariant()}
      {text && (
        <p
          className="mt-4 text-sm font-medium animate-pulse"
          style={{ color: getColorValue(color) }}
        >
          {text}
        </p>
      )}
      {/* Progress bar updates independently via DOM manipulation */}
      <ProgressBarComponent 
        progress={progress} 
        progressText={progressText} 
        color={getColorValue(color)} 
      />
    </div>
  );

  if (fullScreen) {
    return (
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50"
        style={{
          ...style,
          willChange: 'opacity',
        }}
      >
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-200/50 p-8 mx-4 w-96 min-h-[240px]">
          {content}
        </div>
      </div>
    );
  }

  return <div style={style}>{content}</div>;
};

export default WaveLoading;
